<template>
  <page-header :title="jobDetailsState.title" :dateText="dateText">
    <template v-if="showBackButton" slot="navigation-buttons">
      <back-navigation-container></back-navigation-container>
    </template>
    <template v-if="jobLoadingState || profileLoadingState" slot="buttons-set"></template>
    <template v-else-if="isPublishedJob && canInterractWithJob" slot="buttons-set">
      <icon-button v-if="isCandidateUser && recommendedJob"
                   :class="[{small: $mq.phone}, 'dismiss-job-button is-dark paragraph-m']"
                   @click="dismissJob">
        <svg-icon slot="icon" name="delete" width="16" height="16"></svg-icon>
        <div v-translate v-if="!$mq.phone" slot="text">Not interested</div>
      </icon-button>

      <icon-button
        :class="[{small: $mq.phone && isThreeButtonsInSet, 'is-dark': canInterractWithJob, 'is-mid-gray' : !canInterractWithJob}, 'paragraph-m']"
        :disabled="!canInterractWithJob"
        @click="referFriend">
        <div slot="icon">$</div>
        <div v-translate slot="text">Refer a friend</div>
      </icon-button>

      <job-apply-popover
        v-if="featureSwitchesState.QUICK_JOB_APPLY && !isLoggedIn"
        :smallButton="isThreeButtonsInSet"
        position="top-end"
        @on-submit-apply-info="handleJobApply">
      </job-apply-popover>
      <job-apply-button-container v-else-if="!isEmployerUser" :smallButton="isThreeButtonsInSet" :jobApplied="jobApplied" @click="handleJobApply" ></job-apply-button-container>
    </template>

    <template v-else slot="buttons-set">
      <icon-button :disabled="true" class="closed-job-button is-mid-gray paragraph-m">
        <svg-icon slot="icon" name="delete" width="16" height="16"></svg-icon>
        <div v-translate slot="text">Job closed</div>
      </icon-button>
      <job-apply-button-container v-if="isCandidateUser" :smallButton="false" :jobApplied="jobApplied" @click="handleJobApply"></job-apply-button-container>
    </template>

    <template slot="categories">
      <page-header-category v-if="jobDetailsState.annual_salary" :text="formattedSalary">
        <svg-icon slot="icon" name="wallet"></svg-icon>
      </page-header-category>
      <page-header-category v-if="jobDetailsState.location" :text="jobDetailsState.location">
        <svg-icon slot="icon" name="location"></svg-icon>
      </page-header-category>
      <page-header-category
        v-if="jobDetailsState.industry || jobDetailsState.position_function"
        :text="jobDetailsState.industry"
        :qualification="jobDetailsState.position_function">
        <svg-icon slot="icon" name="industry"></svg-icon>
      </page-header-category>
      <page-header-category v-if="jobDetailsState.position_level" :text="jobDetailsState.position_level">
        <manager-icon slot="icon" type="orange"></manager-icon>
      </page-header-category>
    </template>
  </page-header>
</template>

<script>

import 'top20common/distribution/icons/location';
import 'top20common/distribution/icons/industry';
import 'top20common/distribution/icons/wallet';

import {map} from 'lodash';
import ManagerIcon from 'top20common/components/icons/ManagerIcon';
import currencies from 'top20common/distribution/services/currencies';
import redirects from 'top20common/distribution/services/redirects';
import storage from 'top20common/distribution/services/storage';
import {mapGetters} from 'vuex';

import BackNavigationContainer from '@/common/BackNavigationContainer';
import IconButton from '@/common/IconButton';
import PageHeader from '@/common/page-header/PageHeader';
import PageHeaderCategory from '@/common/page-header/PageHeaderCategory';

import JobApplyButtonContainer from './JobApplyButtonContainer';
import JobApplyPopover from './JobApplyPopover';

export default {
  props: {
    showBackButton: Boolean,
    jobApplied: Boolean
  },
  computed: {
    ...mapGetters([
      'jobDetailsState',
      'jobLoadingState',
      'recommendedPublishedJobByCodeState',
      'jobsContainerIdState',
      'featureSwitchesState',
      'canInterractWithJob',
      'isLoggedIn',
      'isCandidateUser',
      'isEmployerUser',
      'isPublishedJob',
      'profileLoadingState'
    ]),
    dateText() {
      if (this.jobDetailsState.published) {
        let label = this.$gettext('Posted on %{date}');
        return this.$gettextInterpolate(label, {
          date: this.$language.getFormattedDate(this.jobDetailsState.published, 'MMMM DD')
        });
      }
    },
    recommendedJob() {
      return this.recommendedPublishedJobByCodeState[this.jobDetailsState.code];
    },
    jobCodeParam() {
      return 'job_code=' + this.jobDetailsState.code;
    },
    queryParamsForReferFriend() {
      let actionQueryParam = 'action=refer';
      return actionQueryParam + '&' + this.queryParamsWithJobCode;
    },
    queryParamsWithJobCode() {
      let params = map(this.$route.query, function(value, key) {
        return key + '=' + value;
      }).join('&');

      return params ? params + '&' + this.jobCodeParam : this.jobCodeParam;
    },
    isThreeButtonsInSet() {
      return !!(this.isCandidateUser && this.recommendedJob);
    },
    formattedSalary() {
      return currencies.getFormattedSalary(
        this.jobDetailsState.annual_salary,
        this.jobDetailsState.annual_salary_currency
      );
    }
  },
  methods: {
    dismissJob() {
      this.$store.dispatch('dismissRecommendedJobAction', this.recommendedJob.id).then(() => {
        this.$toast.open({message: this.$gettext('Job dismissed'), type: 'is-success'});
        this.$router.push({name: 'dashboard'});
      });
    },
    referFriend() {
      this.$mam.trackReferAFriendClicked();
      if (this.isCandidateUser) {
        this.$store.dispatch('copyLinkAction');
        this.$toast.open({message: this.$gettext('Link copied'), type: 'is-success'});
      } else {
        redirects.goToCandidateRegister(
          this.$store,
          this.$route.name,
          this.$route.fullPath,
          this.queryParamsForReferFriend
        );
      }
    },
    jobApply() {
      this.$store
        .dispatch('createJobApplicationAction', {code: this.jobDetailsState.code, query: this.$route.query})
        .then(() => {
          this.$store.commit('mutatePreviousAnchorState', this.jobsContainerIdState);
        })
        .catch(error => {
          this.$toast.open({
            message: error,
            type: 'is-danger',
            duration: 3000
          });
        })
        .finally(() => {
          this.$router.push({name: 'dashboard'});
        });
    },

    preApply() {
      const userData = storage.getJsonItem('register-data');
      this.$store
        .dispatch('createPreApplicationAction', {
          code: this.jobDetailsState.code,
          email: userData.email,
          phone: userData.phone,
          resume_file: this.$store.getters.resumeState.file
        })
        .then(data => {
          const resume = userData.resume ? {...userData.resume, pre_application_id: data.id} : null;
          storage.setJsonItem('register-data', resume ? {...userData, resume} : userData);
          this.goToRegisterPage();
        })
        .catch(error => {
          this.$toast.open({
            message: error,
            type: 'is-danger',
            duration: 3000
          });
        })
        .finally(() => {
          this.goToRegisterPage();
        });
    },
    goToRegisterPage() {
      redirects.goToCandidateRegister(this.$store, this.$route.name, this.$route.fullPath, this.queryParamsWithJobCode);
    },
    handleJobApply() {
      this.isCandidateUser ? this.jobApply() : this.preApply();
    }
  },
  components: {
    PageHeader,
    IconButton,
    PageHeaderCategory,
    JobApplyButtonContainer,
    ManagerIcon,
    BackNavigationContainer,
    JobApplyPopover
  }
};
</script>

<style lang="scss" scoped>
@import '~VariablesStyles';

.buttons-set {
  .popover-content {
    display: flex;
    width: 90px;

    /deep/.v-popover {
      &,
      .trigger {
        width: 100%;
      }
    }
  }
}

/deep/ .fill {
  fill: var(--secondary);
}

/deep/ .dismiss-job-button,
.closed-job-button {
  width: auto;
  svg {
    stroke: var(--white);
  }
}

@media (max-width: $tablet) {
  /deep/ .buttons-set {
    display: flex;
    justify-content: center;
    .button {
      width: auto;
      flex-grow: 0;
    }
  }
}
</style>
